/**
 * Which field feeds which, in both directions.
 *
 * The owner's complaint that this answers: you can see that Strength is 7 and
 * that carry weight is 200, and nothing on the screen says the second is made
 * out of the first. Hovering either one should light up the other.
 *
 * There is exactly one source of truth here and it is the formulas the editor
 * already implements: `computeDerivedBaseStats` (engine/src/stat.cc
 * `critterUpdateDerivedStats`) and `computeSkillValue` (engine/src/skill.cc
 * `skillGetValue`). This module does not restate them — it *names the inputs*,
 * and the UI inverts the map to get the other direction. A linkage that
 * disagreed with the formula would be worse than none, so the shape is chosen to
 * make disagreement hard: derived-stat inputs are listed next to the formula
 * string the editor already shows, and skill inputs are read out of
 * `SKILL_DESCRIPTIONS` rather than typed again.
 *
 * Field keys are the same strings the UI puts on `data-field`, so the hover
 * handler is a map lookup and not a parse.
 */

import { PRIMARY_STAT_COUNT, SKILL_DESCRIPTIONS, SKILL_NAMES, STAT_NAMES } from './constants';

/** `stat:STRENGTH`, `skill:SMALL_GUNS`, `derived:CARRY_WEIGHT`, `pc:LEVEL`. */
export type FieldKey = string;

export const statField = (name: string): FieldKey => `stat:${name}`;
export const derivedField = (name: string): FieldKey => `derived:${name}`;
export const skillField = (name: string): FieldKey => `skill:${name}`;
export const pcField = (name: string): FieldKey => `pc:${name}`;
export const conditionField = (name: string): FieldKey => `condition:${name}`;

/**
 * Inputs of each derived stat, from `critterUpdateDerivedStats`
 * (engine/src/stat.cc). One entry per formula in `DERIVED_BASE_STATS`; a derived
 * stat with no SPECIAL input (BETTER_CRITICALS, which the recompute sets to a
 * flat 0) is present with an empty input list rather than absent, so "this field
 * has no linkage" and "this field was forgotten" are different states.
 */
const DERIVED_INPUTS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['MAXIMUM_HIT_POINTS', ['STRENGTH', 'ENDURANCE']],
  ['MAXIMUM_ACTION_POINTS', ['AGILITY']],
  ['ARMOR_CLASS', ['AGILITY']],
  ['MELEE_DAMAGE', ['STRENGTH']],
  ['CARRY_WEIGHT', ['STRENGTH']],
  ['SEQUENCE', ['PERCEPTION']],
  ['HEALING_RATE', ['ENDURANCE']],
  ['CRITICAL_CHANCE', ['LUCK']],
  ['BETTER_CRITICALS', []],
  ['RADIATION_RESISTANCE', ['ENDURANCE']],
  ['POISON_RESISTANCE', ['ENDURANCE']],
];

/**
 * The full undirected linkage graph: field -> every field it is related to.
 *
 * Undirected on purpose. The owner asked for both directions and there is no
 * reading of "related" under which Strength lights up carry weight but carry
 * weight does not light up Strength — the player is asking the same question
 * from the other end.
 */
function build(): Map<FieldKey, Set<FieldKey>> {
  const graph = new Map<FieldKey, Set<FieldKey>>();
  const link = (a: FieldKey, b: FieldKey) => {
    if (a === b) return;
    for (const [from, to] of [
      [a, b],
      [b, a],
    ] as const) {
      let set = graph.get(from);
      if (!set) graph.set(from, (set = new Set()));
      set.add(to);
    }
  };
  // Every field the UI can hover gets an entry, even an empty one, so the
  // handler never has to distinguish "no links" from "unknown field".
  const seed = (key: FieldKey) => {
    if (!graph.has(key)) graph.set(key, new Set());
  };

  for (let i = 0; i < PRIMARY_STAT_COUNT; i++) seed(statField(STAT_NAMES[i]!));

  for (const [derived, inputs] of DERIVED_INPUTS) {
    seed(derivedField(derived));
    for (const stat of inputs) link(derivedField(derived), statField(stat));
  }

  // Skills: `skillGetValue` adds `statModifier × critterGetStat(stat)` for
  // `stat1` and `stat2`, so those two — and only those two — are the governing
  // stats. Read out of the table rather than restated.
  for (let i = 0; i < SKILL_NAMES.length; i++) {
    const key = skillField(SKILL_NAMES[i]!);
    seed(key);
    const description = SKILL_DESCRIPTIONS[i]!;
    for (const stat of [description.stat1, description.stat2]) {
      if (stat >= 0 && stat < PRIMARY_STAT_COUNT) link(key, statField(STAT_NAMES[stat]!));
    }
  }

  // Hit points is bounded by MAXIMUM_HIT_POINTS, which the character tab shows
  // a field away. That is a real dependency and the one people hit first.
  link(conditionField('hit points'), derivedField('MAXIMUM_HIT_POINTS'));
  // Radiation and poison are resisted by the two derived resistances
  // (engine/src/critter.cc `critterAdjustRadiation` / `critterAdjustPoison`
  // scale the incoming amount by them), so those pair up too.
  link(conditionField('radiation'), derivedField('RADIATION_RESISTANCE'));
  link(conditionField('poison'), derivedField('POISON_RESISTANCE'));

  return graph;
}

const GRAPH = build();

/** Every field related to `key`, in no particular order. Never null. */
export function linkedFields(key: FieldKey): readonly FieldKey[] {
  return Array.from(GRAPH.get(key) ?? []);
}

/** Does this field participate in the linkage graph at all? */
export function isLinkable(key: FieldKey): boolean {
  return GRAPH.has(key);
}

/**
 * The governing stats of a skill, as display text — `PE, IN` — for the skill
 * row's aside. Short codes because the row is already tight.
 */
export function skillStatCodes(skill: number): string {
  const description = SKILL_DESCRIPTIONS[skill];
  if (!description) return '';
  const codes: string[] = [];
  for (const stat of [description.stat1, description.stat2]) {
    if (stat >= 0 && stat < PRIMARY_STAT_COUNT) codes.push(STAT_NAMES[stat]!.slice(0, 2));
  }
  return codes.join(', ');
}
